/**
 * Mensagens de erro amigáveis exibidas ao usuário.
 *
 * Ordem de resolução (ver `shared/utils/apiErrorMessage.ts`):
 *   1. errors.codes.<E_CODE>   — código de erro de domínio do backend (mais específico)
 *   2. errors.validation       — validação 422 do VineJS (campos)
 *   3. errors.status.<http>    — fallback por status HTTP
 *   4. errors.network          — falha de conexão/fetch
 *   5. errors.generic          — último recurso
 *
 * Códigos não mapeados caem na mensagem por status HTTP, então sempre fica
 * amigável mesmo sem entrada dedicada. Para adicionar uma mensagem, use o
 * `code` do backend (a string `E_...`) como chave em `codes` aqui e em `en`.
 */
const errors = {
  title: "Erro",
  generic: "Algo deu errado. Tente novamente.",
  network: "Falha na conexão. Verifique sua internet e tente novamente.",
  timeout: "A requisição demorou demais. Tente novamente.",
  validation: "Revise os campos destacados e tente novamente.",
  rateLimited: "Muitas tentativas. Aguarde um momento e tente novamente.",

  status: {
    "400": "Não foi possível processar a requisição. Verifique os dados e tente novamente.",
    "401": "Sua sessão expirou. Entre novamente.",
    "403": "Você não tem permissão para realizar esta ação.",
    "404": "Não encontramos o que você procura.",
    "409": "Esta ação conflita com o estado atual. Atualize a página e tente novamente.",
    "413": "O arquivo é muito grande.",
    "422": "Revise os campos destacados e tente novamente.",
    "429": "Muitas tentativas. Aguarde um momento e tente novamente.",
    "500": "Algo deu errado do nosso lado. Tente novamente em instantes.",
    "502": "O serviço está temporariamente indisponível. Tente novamente em instantes.",
    "503": "O serviço está temporariamente indisponível. Tente novamente em instantes.",
  },

  codes: {
    // Autenticação
    E_INVALID_CREDENTIALS: "E-mail ou senha inválidos.",
    E_INVALID_TOKEN: "Sua sessão expirou. Entre novamente.",
    E_USER_PROFILE_MISSING: "Não encontramos seu perfil. Entre em contato com o suporte.",
    E_OAUTH_EXCHANGE_FAILED: "Não foi possível concluir o login. Tente novamente.",
    E_INSUFFICIENT_ROLE: "Você não tem permissão para realizar esta ação.",
    E_INSUFFICIENT_SCOPE: "Você não tem acesso a este recurso.",
    E_INVALID_PASSWORD_RESET_TOKEN: "Este link de redefinição de senha é inválido ou expirou.",
    E_EMAIL_SEND_FAILED: "Não foi possível enviar o e-mail. Tente novamente em instantes.",

    // Convites e cadastro
    E_INVITE_NOT_FOUND: "Este convite não existe mais.",
    E_INVITE_TOKEN_REQUIRED: "O link do convite está incompleto.",
    E_INVITE_TOKEN_INVALID: "Este link de convite é inválido ou expirou.",
    E_REGISTRATION_FAILED: "Não foi possível concluir seu cadastro. Tente novamente.",
    E_PASSWORD_TOO_LONG: "A senha é muito longa.",
    E_USER_ALREADY_EXISTS: "Já existe uma conta com este e-mail.",
    E_USER_IN_ANOTHER_ORG: "Este usuário já pertence a outra organização.",
    E_INVITE_ALREADY_EXISTS: "Já existe um convite enviado para este e-mail.",
    E_SUBSCRIPTION_REQUIRED: "É necessária uma assinatura ativa para esta ação.",
    E_USER_LIMIT_REACHED: "Você atingiu o limite de usuários do seu plano.",

    // Usuários
    E_INVALID_DELETION_PAYLOAD: "Confirme sua senha para excluir a conta.",
    E_USER_NOT_FOUND: "Não encontramos este usuário.",
    E_EMAIL_IN_USE: "Este e-mail já está em uso.",
    E_EMPTY_UPDATE: "Não há alterações para salvar.",
    E_CANNOT_ASSIGN_SUPERADMIN: "Você não pode atribuir o papel de super admin.",
    E_CANNOT_MODIFY_SUPERADMIN: "Você não pode modificar um super admin.",
    E_AVATAR_INVALID: "Esta imagem não é válida. Use um arquivo PNG ou JPG.",
    E_AVATAR_PERMISSION_DENIED: "Você não tem permissão para alterar este avatar.",
    E_INVALID_USER_SETTINGS_PAYLOAD: "Algumas configurações são inválidas. Revise e tente novamente.",

    // Produtos e organizações
    E_PRODUCT_ACCESS_DENIED: "Você não tem acesso a este workspace.",
    E_PRODUCT_NOT_FOUND: "Não encontramos este workspace.",
    E_USER_ACCESS_DENIED: "Você não tem acesso a este recurso.",
    E_USER_ORGANIZATION_MISSING: "Sua conta não está vinculada a uma organização.",
    E_ORGANIZATION_REQUIRED: "Selecione uma organização para continuar.",
    E_ORGANIZATION_NOT_FOUND: "Não encontramos esta organização.",
    E_ORGANIZATION_ACCESS_DENIED: "Você não tem acesso a esta organização.",
    E_ORGANIZATION_UPDATE_FORBIDDEN: "Você não tem permissão para atualizar esta organização.",
    E_ORGANIZATION_DELETE_FORBIDDEN: "Você não tem permissão para excluir esta organização.",
    E_ORGANIZATION_HAS_PRODUCTS: "Remova os workspaces antes de excluir esta organização.",
    E_ORGANIZATION_HAS_DEPENDENCIES: "Esta organização ainda tem dados vinculados e não pode ser excluída.",
    E_EMBEDDING_MODEL_INCOMPATIBLE:
      "Este modelo de embedding gera vetores de um tamanho que o Brain não armazena.",
    E_LAST_ADMIN_REMOVAL_FORBIDDEN: "Você não pode remover o último admin deste workspace.",
    E_PRODUCT_DELETION_BLOCKED: "Este workspace não pode ser excluído enquanto tiver dados vinculados.",
    E_PRODUCT_MEMBERSHIP_CONFLICT: "Este usuário já é membro do workspace.",
    E_INVALID_ORGANIZATION_OVERRIDE: "A organização selecionada é inválida.",
    E_LOGO_INVALID: "Esta imagem de logo não é válida. Use um arquivo PNG ou JPG.",
    E_LOGO_UPLOAD_FAILED: "Não foi possível enviar o logo. Tente novamente.",

    // Integrações
    E_INTEGRATION_NOT_FOUND: "Não encontramos esta ferramenta.",
    E_INTEGRATION_TEMPLATE_NOT_FOUND: "Não encontramos este modelo de ferramenta.",
    E_INTEGRATION_TEMPLATE_HAS_INSTANCES: "Remova as ferramentas instaladas antes de excluir este modelo.",
    E_INTEGRATION_BASE_NOT_FOUND: "Não encontramos esta ferramenta.",
    E_INTEGRATION_BASE_ACCESS_DENIED: "Você não tem acesso a esta ferramenta.",
    E_INTEGRATION_BASE_ALREADY_EXISTS: "Esta ferramenta já está instalada.",
    E_INTEGRATION_BASE_IN_USE:
      "Esta ferramenta está em uso como provedor de IA. Escolha outro provedor antes de excluí-la.",
    E_INTEGRATION_CREDENTIALS_REJECTED:
      "O provedor recusou as credenciais informadas. Confira a chave e os demais campos antes de salvar.",
    E_PLATFORM_ORGANIZATION_MISSING:
      "As configurações de IA da plataforma ainda não estão disponíveis neste ambiente.",
    E_PLATFORM_INTEGRATION_UNAVAILABLE:
      "Esta ferramenta não pertence à plataforma. Cadastre-a nas ferramentas da plataforma.",
    E_PLATFORM_MODEL_NOT_CONFIGURED:
      "Nenhum modelo de IA da plataforma está configurado para esta finalidade. Configure-o na área de Plataforma.",
    E_PLATFORM_MODEL_UNRESOLVED:
      "O provedor de IA escolhido para esta finalidade não pôde ser usado. Revise o cadastro na área de Plataforma.",
    E_INVALID_INTEGRATION_CREDENTIALS: "As credenciais são inválidas. Verifique e tente novamente.",
    E_UNSUPPORTED_AUTH_TYPE: "Este tipo de autenticação não é suportado.",
    E_DECRYPTION_FAILED: "Não foi possível ler as credenciais armazenadas. Informe-as novamente.",

    // Base de conhecimento
    E_KNOWLEDGE_BASE_NOT_FOUND: "Não encontramos este Brain.",
    E_KNOWLEDGE_BASE_ACCESS_DENIED: "Você não tem acesso a este Brain.",
    E_KNOWLEDGE_BASE_HAS_CONTENT: "Remova os documentos antes de excluir este Brain.",
    E_EMBEDDING_PROVIDER_UNAVAILABLE: "O serviço de embeddings está indisponível agora. Tente novamente em instantes.",
    E_UNSUPPORTED_FILE_TYPE: "Este tipo de arquivo não é suportado.",
    E_CONTENT_QUALITY_REJECTED: "Não foi possível processar este conteúdo. Tente outro arquivo.",
    E_EMBEDDING_NOT_FOUND: "Não encontramos este embedding.",
    E_VECTOR_SEARCH_FAILED: "A busca falhou. Tente novamente.",

    // Armazenamento
    E_STORAGE_UPLOAD_FAILED: "Não foi possível enviar o arquivo. Tente novamente.",
    E_STORAGE_REMOVE_FAILED: "Não foi possível excluir o arquivo. Tente novamente.",
    E_STORAGE_ITEM_NOT_FOUND: "Não encontramos este arquivo.",
    E_STORAGE_INVALID_FILE: "Este arquivo não é válido.",
    E_STORAGE_INGESTION_FAILED: "Não foi possível processar este arquivo. Tente novamente.",

    // Workflows
    E_AGENT_TEMPLATE_NOT_FOUND: "Não encontramos este modelo de workflow.",
    E_AGENT_BASE_NOT_FOUND: "Não encontramos este workflow.",
    E_AGENT_BASE_ACCESS_DENIED: "Você não tem acesso a este workflow.",
    E_AGENT_ALREADY_INSTALLED: "Este workflow já está instalado.",
    E_AGENT_VERSION_CONFLICT: "Este workflow foi atualizado em outro lugar. Atualize a página e tente novamente.",

    // Chat e playground
    E_CHAT_NOT_FOUND: "Não encontramos esta conversa.",
    E_CHAT_ACCESS_DENIED: "Você não tem acesso a esta conversa.",
    E_CHAT_STREAM_UNAVAILABLE: "O serviço de chat está indisponível agora. Tente novamente em instantes.",
    E_AGENTIC_SERVICE_UNAVAILABLE: "O assistente está indisponível agora. Tente novamente em instantes.",
    E_PLAYGROUND_CHAT_PAYMENT_REQUIRED: "É necessária uma assinatura ativa para usar o playground.",

    // Assinaturas e faturas
    E_SUBSCRIPTION_NOT_FOUND: "Não encontramos uma assinatura.",
    E_SUBSCRIPTION_NOT_ACTIVE: "Sua assinatura não está ativa.",
    E_NO_SUBSCRIPTION_FOR_ORG: "Esta organização não tem uma assinatura ativa.",
    E_INVOICE_NOT_FOUND: "Não encontramos esta fatura.",
    E_INVOICE_ALREADY_PAID: "Esta fatura já foi paga.",
    E_PAYMENT_FAILED: "O pagamento não pôde ser processado. Tente novamente.",

    // Workflows
    E_WORKFLOW_NOT_FOUND: "Não encontramos este workflow.",
    E_WORKFLOW_BASE_NOT_FOUND: "Não encontramos este workflow.",
    E_NODE_NOT_FOUND: "Não encontramos este nó.",
    E_TRIGGER_NOT_FOUND: "Não encontramos este gatilho.",

    // Configurações
    E_SETTING_NOT_FOUND: "Não encontramos esta configuração.",
    E_CONTACT_ALREADY_EXISTS: "Este contato já existe.",

    // Facets
    E_FACET_NOT_FOUND: "Não encontramos esta facet.",
    E_FACET_CONFLICT: "Já existe uma facet com este nome e valor nesta organização.",
    E_FACET_VALIDATION_FAILED: "Alguns campos da facet são inválidos. Revise e tente novamente.",
    E_FACET_PARENT_INVALID: "A facet pai selecionada é inválida.",
    E_FACET_CYCLE_DETECTED: "Isso criaria um ciclo na hierarquia de facets.",
    E_FACET_IN_USE: "Esta facet está em uso e não pode ser excluída.",
    E_FACET_DEPTH_EXCEEDED: "A hierarquia de facets é muito profunda.",

    // Communicator
    E_COMMUNICATOR_INTEGRATION_NOT_FOUND: "Não encontramos este canal de comunicação.",
    E_COMMUNICATOR_LINK_INVALID: "Este link é inválido.",
    E_COMMUNICATOR_LINK_EXPIRED: "Este link expirou.",

    // Serviço de e-mail
    E_EMAIL_SERVICE_CONNECTION_FAILED: "Não foi possível conectar ao provedor de e-mail. Verifique as configurações.",
    E_EMAIL_SERVICE_SEND_FAILED: "Não foi possível enviar o e-mail. Tente novamente em instantes.",

    // Upstream / proxy
    E_UPSTREAM_UNAVAILABLE: "O serviço está temporariamente indisponível. Tente novamente em instantes.",

    // Genéricos de cliente (guards do front: resposta malformada, stream quebrado)
    E_UNEXPECTED_RESPONSE: "Resposta inesperada do servidor. Tente novamente.",
    E_INSTALL_STREAM_FAILED: "Não foi possível concluir a instalação. Tente novamente.",
  },
} as const;

export default errors;
